import { AnyAction, createSlice } from '@reduxjs/toolkit'
// import { jwtDecode } from 'jwt-decode'
import { ResponseType } from '../../interfaces/response';
import { confirmAccount, createNewPassword, signin, signup, verificationAccount, verifyEmail } from '../../data-access/apis/authentication/auth';
import { contactUs } from '../../data-access/apis/common/contact';
import { addToCart, GetAddedAllCarts, removeFromCart } from '../../data-access/apis/common/Carts';
import { searchAmazonItems } from '../../data-access/apis/authentication/amazon/common';
import { buyNowCart } from '../../data-access/apis/modals/buyNow';
import { getProfilePhoto, deleteProfilePhoto, uploadProfilePhoto, updateUserProfile, getUserProfile, deleteUserAccount } from '../../data-access/apis/common/profile';

interface MessageState {
  status: ResponseType;
  statusCode: number | null;
  otherStatusCode: number | null;
  message: string | null;
  item: any;
  items: any;
  toke: string | null;
  error: string | null; // ✅ explicitly string | null
  errors: any;
  loading: boolean;
  token: string | null;
  user: any;
}

const messageLifeCycles = createSlice({
  name: 'messages',
  initialState: {
    status: ResponseType.Pending,
    statusCode: null,
    otherStatusCode: null,
    message: null,
    item: null,
    items: null,
    toke: null,
    error: null,
    errors: null,
    loading: false,
    token: null,
    user: null,
  } as MessageState, // 👈 Type it properly here,
  reducers: {
    resetMessage: (state) => {
      state.status = ResponseType.Pending;
      state.statusCode = null;
      state.otherStatusCode = null;
      state.message = null;
      state.item = null;
      state.items = null;
      state.toke = null;
      state.error = null;
      state.errors = null;
      state.loading = false;
      state.token = null;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.loading = true
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false
        state.message = action.payload.message
        state.statusCode = action.payload.status
        state.status = ResponseType.Success
      })
      .addCase(signup.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.error = action.payload.data?.message
        state.status = ResponseType.Failed
        state.statusCode = action.payload.status
      })
      .addCase(confirmAccount.pending, (state) => {
        state.loading = true
      })
      .addCase(confirmAccount.fulfilled, (state, action) => {
        state.loading = false
        state.message = action.payload?.message
        state.statusCode = action.payload.statusCode
        state.status = ResponseType.Success
      })
      .addCase(confirmAccount.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.error = action.payload.data?.message
        state.status = ResponseType.Failed
        state.statusCode = action.payload.status
      })
      .addCase(signin.pending, (state) => {
        state.loading = true
      })
      .addCase(signin.fulfilled, (state, action) => {
        state.loading = false
        state.statusCode = action.payload.status
        state.message = action.payload?.message
        state.status = ResponseType.Success
      })
      .addCase(signin.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.error = action.payload.data?.message
        state.status = ResponseType.Failed
        state.statusCode = action.payload.data?.status
      })
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false
        state.message = action.payload.message
        state.statusCode = action.payload.status
        state.status = ResponseType.Success
      })
      .addCase(verifyEmail.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.statusCode = action.payload.data?.status
        state.error = action.payload.data?.message
        state.status = ResponseType.Failed
      })
      .addCase(verificationAccount.pending, (state) => {
        state.loading = true
      })
      .addCase(verificationAccount.fulfilled, (state, action) => {
        state.loading = false
        state.message = action.payload.message
        state.statusCode = action.payload.status
        state.status = ResponseType.Success
      })
      .addCase(verificationAccount.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.statusCode = action.payload.data?.status
        state.error = action.payload.data?.message
        state.status = ResponseType.Failed
      })
      .addCase(createNewPassword.pending, (state) => {
        state.loading = true
      })
      .addCase(createNewPassword.fulfilled, (state, action) => {
        state.loading = false
        state.message = action.payload.message
        state.statusCode = action.payload.status
        state.status = ResponseType.Success
      })
      .addCase(createNewPassword.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.statusCode = action.payload.data?.status
        state.error = action.payload.data?.message
        state.status = ResponseType.Failed
      })
      .addCase(contactUs.pending, (state) => {
        state.loading = true
      })
      .addCase(contactUs.fulfilled, (state, action: AnyAction) => {
        state.loading = false
        state.message = action.payload.message
        state.statusCode = action.payload.status
        state.status = ResponseType.Success
      })
      .addCase(contactUs.rejected, (state, action: AnyAction) => {
        state.loading = false
        state.statusCode = action.payload.status
        state.error = action.payload?.data?.message
        state.status = ResponseType.Failed
      })
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToCart.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;

        // 🛒 Push product to cartItems from response data
        if (action.payload && action.payload.data) {
          // const productData = action.payload.data;
          state.message = action.payload.message;
        }
      })
      .addCase(addToCart.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;

        const backendMessage = (action.payload?.message || action.payload || '').toString().trim();

        // Normalize both strings before comparing
        const normalizedMessage = backendMessage.normalize("NFKC");

        if (normalizedMessage === 'Invalid authorization token supplied') {
          state.error = 'Login is required to add items to the cart.';
        } else {
          state.error = backendMessage || 'Add to cart failed.';
        }

        state.errors = action.error?.message || null;
      })
      .addCase(GetAddedAllCarts.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetAddedAllCarts.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload.message;
      })
      .addCase(GetAddedAllCarts.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;

        const backendMessage = (action.payload?.message || action.payload || '').toString().trim();
        const normalizedMessage = backendMessage.normalize('NFKC');

        state.error = normalizedMessage || 'Failed to fetch cart items.';
        state.errors = action.error?.message || null;
      })
      .addCase(searchAmazonItems.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
        state.message = null;
        state.error = null;
      })
      .addCase(searchAmazonItems.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.statusCode = action.payload.status;
        state.items = action.payload.data; // assuming data contains list of items
        state.message = action.payload.message;
      })
      .addCase(searchAmazonItems.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;

        const rawPayload = action.payload;

        let errorMessage = 'Something went wrong';

        if (typeof rawPayload === 'string') {
          errorMessage = rawPayload;
        } else if (rawPayload?.message) {
          errorMessage = rawPayload.message;
        } else if (rawPayload?.error) {
          errorMessage = rawPayload.error;
        }

        const normalizedMessage = errorMessage.toString().normalize('NFKC');

        state.error = normalizedMessage;
        state.errors = action.error?.message || null;
      })
      // 👇 extraReducers me yeh add karo
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeFromCart.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload.message || 'Product removed from cart.';

        // const removedId = action.payload.productId;
        // state.cartItems = state.cartItems.filter(item => item.productId !== removedId);
      })
      .addCase(removeFromCart.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = (action.payload?.message || 'Remove from cart failed.').toString().trim();
        state.errors = action.error?.message || null;
      })
      .addCase(buyNowCart.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(buyNowCart.fulfilled, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload.message;
        state.statusCode = action.payload.status;
      })
      .addCase(buyNowCart.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.statusCode = action.payload?.status || 500;
        state.error = action.payload?.message || 'Something went wrong';
        state.errors = action.error?.message || null;
      })
      .addCase(getProfilePhoto.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(getProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;

        const profilePhoto = action.payload?.profilePhoto;

        // ✅ Set in user object or in state as needed
        if (state.user) {
          state.user.profileImage = profilePhoto || null;
        } else {
          state.user = {
            _id: "", // Provide fallbacks or actual values if needed
            name: "",
            username: "",
            email: "",
            phone: "",
            profileImage: profilePhoto || null,
          };
        }

        state.status = ResponseType.Success;
      })
      .addCase(getProfilePhoto.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.error = action.payload;
        state.status = ResponseType.Failed;
      })
      .addCase(deleteProfilePhoto.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(deleteProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload?.message;

        // ✅ Backend se updated user aaya hai
        const user = action.payload?.user;

        if (state.user) {
          state.user.profileImage = user?.profilePhoto || null;
        } else {
          state.user = {
            _id: user?._id || "",
            name: user?.name || "",
            username: user?.username || "",
            email: user?.email || "",
            phone: user?.phone || "",
            profileImage: user?.profilePhoto || null,
          };
        }
      })
      .addCase(deleteProfilePhoto.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      })
      .addCase(uploadProfilePhoto.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload.user; // ✅ directly from API response

        state.user = {
          _id: user._id,
          name: user.name,
          username: user.username,
          email: user.email,
          phone: user.phone,
          profileImage: action.payload.profilePhoto || null, // ✅ set new photo
        };

        state.message = action.payload.message;
        state.status = ResponseType.Success;
      })
      .addCase(uploadProfilePhoto.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.error = action.payload?.message;
        state.status = ResponseType.Failed;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload?.user;
        if (user) {
          state.user = {
            _id: user._id,
            name: user.name,
            username: user.username,
            email: user.email,
            phone: user.phone,
            profileImage: user.profilePhoto || null,
          };
        }
        state.message = action.payload?.message;
        state.status = ResponseType.Success;
      })
      .addCase(updateUserProfile.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      })
      .addCase(getUserProfile.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.user = action.payload.user;
        state.message = action.payload.message;
      })
      .addCase(getUserProfile.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      })
      .addCase(deleteUserAccount.pending, (state) => {
        state.loading = true;
        state.status = ResponseType.Pending;
      })
      .addCase(deleteUserAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.status = ResponseType.Success;
        state.message = action.payload.message;
        state.user = null; // account delete hone ke baad user data clear
      })
      .addCase(deleteUserAccount.rejected, (state, action: AnyAction) => {
        state.loading = false;
        state.status = ResponseType.Failed;
        state.error = action.payload?.message;
      });
  }
})

export const { resetMessage } = messageLifeCycles.actions;
export default messageLifeCycles.reducer
